import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ConnectionStatus } from '@modeso/aumico-lib-core-fe';
import { Paths } from '../../../enums/paths.enum';
import {
    AumicoCompaniesService,
    BexioConnectionStatusDto,
    IntegrationLeaderboardDto,
    BexioProvider,
    BexioService,
    BexioState,
} from '@modeso/aumico-lib-companies-fe';
import {
    IConnectionMapping,
    IntegrationProvider,
    IntegrationVendor,
    PerCompanyVendorState,
    VendorDefinition,
} from '@modeso/aumico-lib-integrations-fe';
import { BasePageComponent } from '../../../pages/base.page';

interface IntegrationLeaderboardItemViewModel extends IntegrationLeaderboardDto {
    hasVoted: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-company-integrations-page',
    templateUrl: 'company-integrations.page.html',
    styleUrls: ['company-integrations.page.scss'],
})
export class CompanyIntegrationsPage extends BasePageComponent implements OnInit, OnDestroy {
    bexioConnectionStatus$: Observable<BexioState['connectionStatus']>;
    bexioConnection$: Observable<BexioConnectionStatusDto | null>;

    integrationLeaderboard: IntegrationLeaderboardItemViewModel[] = [];
    isLeaderboardLoading = false;
    votingIntegration: string | null = null;
    showVoteModal = false;
    voteModalIntegration: string | null = null;
    voteModalMessage = '';

    showSuggestionModal = false;
    suggestionMessage = '';
    isSubmittingSuggestion = false;

    /** Per-org integration mappings of this company (today: D365 BC). Drives the new overview. */
    companyConnectionMappings: IConnectionMapping[] = [];

    /** Per-company vendor state (Bexio) handed to the unified overview, keyed by vendor. */
    perCompanyVendorState: Record<string, PerCompanyVendorState> = {};

    /** Bexio info-modal (opened from the unified card's Info button). */
    showBexioInfoModal = false;

    /** Latest Bexio connected-ness, mirrored for synchronous modal branching. */
    private bexioConnectedNow = false;

    /** Mirror of IntegrationVendor for template comparisons. */
    readonly IntegrationVendor = IntegrationVendor;

    /** True when the user is an org/app admin — only they can reach the Org Settings. */
    isAdmin$: Observable<boolean>;

    public companyId: string;

    constructor(
        injector: Injector,
        private bexioProvider: BexioProvider,
        private bexioService: BexioService,
        private companiesService: AumicoCompaniesService,
        private integrationProvider: IntegrationProvider,
    ) {
        super(injector);
        this.bexioConnectionStatus$ = this.bexioProvider.getConnectionStatus$();
        this.bexioConnection$ = this.bexioProvider.getConnection$();
        this.isAdmin$ = this.authProvider.getIsAdmin$();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.companyId = this.activatedRoute.snapshot.parent?.params['companyId'];
        if (this.companyId) {
            this.bexioProvider.loadConnection(this.companyId);
            // Per-org integrations (BC): the company carries its mappings inline.
            // Load org connections too so the redirect modal can resolve tenant names.
            this.integrationProvider.loadConnections();
            this.loadCompanyConnectionMappings(this.companyId);
        }
        this.loadIntegrationLeaderboard();

        // Feed Bexio's per-company state into the unified overview. The provider
        // normalizes Bexio's status onto the shared ConnectionStatus enum, so we
        // no longer translate raw Bexio strings here. The same stream also drives
        // `bexioConnectedNow` for the modal's primary action (connect vs disconnect).
        this.bexioProvider
            .getNormalizedConnectionStatus$()
            .pipe(untilDestroyed(this))
            .subscribe((status) => {
                this.bexioConnectedNow = status === ConnectionStatus.CONNECTED;
                this.perCompanyVendorState = {
                    ...this.perCompanyVendorState,
                    [IntegrationVendor.BEXIO]: {
                        connected: status === ConnectionStatus.CONNECTED,
                        needsAction:
                            status === ConnectionStatus.EXPIRED || status === ConnectionStatus.ERROR,
                    },
                };
            });
    }

    /** The unified overview delegates per-company vendor Info clicks here. */
    public onPerCompanyInfo(vendor: VendorDefinition): void {
        if (vendor.vendor === IntegrationVendor.BEXIO) {
            this.showBexioInfoModal = true;
        }
    }

    /** Admin chose to manage a per-org integration in the Org Settings. */
    public onGoToOrgSettings(): void {
        const locale = this.router.url.split('/')[1];
        void this.router.navigate([
            `${locale}/${Paths.HOME}/${Paths.ORGANIZATION}/${Paths.ORGANIZATION_SETTINGS}/${Paths.INTEGRATIONS}`,
        ]);
    }

    public get isBexioConnected(): boolean {
        return this.bexioConnectedNow;
    }

    /**
     * Primary action of the Bexio info modal: connect when not connected, disconnect
     * when connected. Both flows are owned by BexioProvider (companies-fe).
     */
    public onBexioModalPrimary(): void {
        if (this.isBexioConnected) {
            this.onBexioDisconnect();
        } else {
            this.onBexioConnect();
        }
        this.showBexioInfoModal = false;
    }

    public onCloseBexioInfoModal(): void {
        this.showBexioInfoModal = false;
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.bexioProvider.cancelConnect();
    }

    public onBexioConnect(): void {
        if (this.companyId) {
            this.bexioProvider.connect(this.companyId);
        }
    }

    public onBexioDisconnect(): void {
        if (this.companyId) {
            this.bexioProvider.disconnect(this.companyId);
        }
    }

    /** Highest vote count, recomputed whenever the leaderboard changes (see sortLeaderboard). */
    public maxVoteCount = 0;

    public getVoteBarWidth(votes: number): number {
        if (this.maxVoteCount === 0) return 0;
        return (votes / this.maxVoteCount) * 100;
    }

    public onOpenVoteModal(integration: string): void {
        const currentState = this.integrationLeaderboard.find((item) => item.integration === integration);
        if (currentState == null || currentState.hasVoted || this.votingIntegration != null) return;

        this.voteModalIntegration = integration;
        this.voteModalMessage = '';
        this.showVoteModal = true;
    }

    public onCancelVoteModal(): void {
        this.showVoteModal = false;
        this.voteModalIntegration = null;
        this.voteModalMessage = '';
    }

    public onVoteForIntegration(): void {
        if (this.votingIntegration != null) return;
        if (this.voteModalIntegration == null) return;

        const integration = this.voteModalIntegration;

        const currentState = this.integrationLeaderboard.find((item) => item.integration === integration);
        if (currentState == null || currentState.hasVoted) return;

        const previousState = this.integrationLeaderboard.map((item) => ({ ...item }));
        const voteMessage = this.voteModalMessage.trim() || undefined;
        this.votingIntegration = integration;

        this.integrationLeaderboard = this.integrationLeaderboard.map((item) =>
            item.integration === integration ? { ...item, hasVoted: true, votes: item.votes + 1 } : item,
        );
        this.sortLeaderboard();
        this.onCancelVoteModal();

        this.bexioService
            .voteForIntegration(integration, voteMessage)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.votingIntegration = null;
                },
                error: () => {
                    this.integrationLeaderboard = previousState;
                    this.sortLeaderboard();
                    this.votingIntegration = null;
                },
            });
    }

    public onOpenSuggestionModal(): void {
        this.suggestionMessage = '';
        this.showSuggestionModal = true;
    }

    public onCancelSuggestionModal(): void {
        this.showSuggestionModal = false;
        this.suggestionMessage = '';
    }

    public onSubmitSuggestion(): void {
        if (this.isSubmittingSuggestion || this.suggestionMessage.trim().length === 0) return;

        this.isSubmittingSuggestion = true;
        this.bexioService
            .sendIntegrationRequest('other', this.suggestionMessage.trim())
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.isSubmittingSuggestion = false;
                    this.onCancelSuggestionModal();
                },
                error: () => {
                    this.isSubmittingSuggestion = false;
                },
            });
    }

    /**
     * Reads the company's `integrations.connectionMappings` inline from the full
     * company DTO (same path the AR-import view uses). Feeds the per-org integrations
     * overview so it can split connected/available without a dedicated endpoint.
     */
    private loadCompanyConnectionMappings(companyId: string): void {
        this.companiesService
            .getCompanyById(companyId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (fullCompany) => {
                    this.companyConnectionMappings =
                        (fullCompany as unknown as {
                            integrations?: { connectionMappings?: IConnectionMapping[] };
                        })?.integrations?.connectionMappings ?? [];
                },
                error: () => {
                    this.companyConnectionMappings = [];
                },
            });
    }

    private loadIntegrationLeaderboard(): void {
        this.isLeaderboardLoading = true;

        forkJoin({
            leaderboard: this.bexioService.getIntegrationLeaderboard(),
            myVotes: this.bexioService.getMyIntegrationVotes(),
        })
            .pipe(untilDestroyed(this))
            .subscribe({
                next: ({ leaderboard, myVotes }) => {
                    const votedIntegrationSet = new Set(myVotes.integrations);
                    this.integrationLeaderboard = leaderboard.map((item) => ({
                        ...item,
                        hasVoted: votedIntegrationSet.has(item.integration),
                    }));
                    this.sortLeaderboard();
                    this.isLeaderboardLoading = false;
                },
                error: () => {
                    this.isLeaderboardLoading = false;
                },
            });
    }

    private sortLeaderboard(): void {
        this.integrationLeaderboard = [...this.integrationLeaderboard].sort(
            (a, b) => b.votes - a.votes || a.integration.localeCompare(b.integration),
        );
        this.maxVoteCount = this.integrationLeaderboard.reduce((max, item) => Math.max(max, item.votes), 0);
    }
}
